import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation } from "react-router-dom";

const IdlePopup = () => {
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    number: "",
    message: "",
    captcha: "",
  });
  const [captchaCode, setCaptchaCode] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const server_url = localStorage.getItem("server_url");

  // Refs
  const idleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const popupOpenRef = useRef(false);
  const isExcludedRef = useRef(false);
  const scrollAnimationFrameRef = useRef(null);

  // Session storage key for tracking closed popups per page
  const POPUP_CLOSED_KEY = 'idle_popup_closed_pages';

  // Check if current path is login or registration
  const isExcludedPage = () => {
    const excludedPaths = ["/Login", "/registration", "/Logout", "/PromotionalImage"];
    return excludedPaths.some((path) => location.pathname.startsWith(path));
  };

  // Check if popup was closed on current page
  const isPopupClosedOnCurrentPage = () => {
    try {
      const closedPages = JSON.parse(sessionStorage.getItem(POPUP_CLOSED_KEY) || '[]');
      return closedPages.includes(location.pathname);
    } catch (error) {
      console.error("Error reading sessionStorage:", error);
      return false;
    }
  };

  // Mark popup as closed on current page
  const markPopupClosedOnCurrentPage = () => {
    try {
      const closedPages = JSON.parse(sessionStorage.getItem(POPUP_CLOSED_KEY) || '[]');
      if (!closedPages.includes(location.pathname)) {
        closedPages.push(location.pathname);
        sessionStorage.setItem(POPUP_CLOSED_KEY, JSON.stringify(closedPages));
      }
    } catch (error) {
      console.error("Error writing to sessionStorage:", error);
    }
  };

  // Clear closed pages when navigating to a new page (optional)
  const clearClosedPages = () => {
    sessionStorage.removeItem(POPUP_CLOSED_KEY);
  };

  // Generate random CAPTCHA code
  const generateCaptcha = () => {
    const chars = "abcdefghijkmnpqrstuvwxyz0123456789";
    let code = "";
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(code);
    setFormData((prev) => ({ ...prev, captcha: "" }));
  };

  // Reset the idle timer
  const resetIdleTimer = () => {
    // Clear existing timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    // Don't start timer if:
    // 1. On excluded page
    // 2. Already submitted
    // 3. Popup is currently open
    // 4. Popup was closed on current page
    if (isExcludedRef.current || isSubmitted || popupOpenRef.current || isPopupClosedOnCurrentPage()) {
      return;
    }

    // Update last activity time
    lastActivityRef.current = Date.now();

    // Set new timer for 10 seconds
    idleTimerRef.current = setTimeout(() => {
      // Check if user has been inactive for 10 seconds
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity >= 10000 && document.visibilityState === "visible") {
        setShowPopup(true);
        popupOpenRef.current = true;
      }
    }, 10000);
  };

  // Handle user activity
  const handleUserActivity = () => {
    // Update last activity time
    lastActivityRef.current = Date.now();
    
    // Only reset timer if popup is not open and not closed on this page
    if (!popupOpenRef.current && !isPopupClosedOnCurrentPage()) {
      resetIdleTimer();
    }
  };

  // Handle scroll with debouncing
  const handleScroll = () => {
    // Use requestAnimationFrame for smoother performance
    if (scrollAnimationFrameRef.current) {
      cancelAnimationFrame(scrollAnimationFrameRef.current);
    }
    
    scrollAnimationFrameRef.current = requestAnimationFrame(() => {
      handleUserActivity();
    });
  };

  // Handle visibility change (tab switch)
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      // User returned to tab, check if they were idle
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity >= 10000 && !popupOpenRef.current && !isPopupClosedOnCurrentPage()) {
        setShowPopup(true);
        popupOpenRef.current = true;
      } else {
        resetIdleTimer();
      }
    } else {
      // User left tab, clear timer
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    }
  };

  // Initialize CAPTCHA on component mount
  useEffect(() => {
    generateCaptcha();

    // Check if already submitted in this session
    const submitted = localStorage.getItem("inquiry_submitted");
    if (submitted === "true") {
      setIsSubmitted(true);
    }
  }, []);

  // Clear closed pages when location changes (optional)
  useEffect(() => {
    // Uncomment this line if you want to show popup again when user navigates to a new page
    // clearClosedPages();
  }, [location.pathname]);

  // Handle idle timer setup and cleanup
  useEffect(() => {
    // Check if current page is excluded
    isExcludedRef.current = isExcludedPage();
    
    // Don't set up listeners if on excluded page or already submitted
    if (isExcludedRef.current || isSubmitted) {
      return;
    }

    // Start the initial idle timer
    resetIdleTimer();

    // Define activity events to track
    const activityEvents = [
      "mousemove",
      "mousedown",
      "click",
      "scroll",
      "keydown",
      "keypress",
      "keyup",
      "touchstart",
      "touchmove",
      "input",
      "change",
      "focus",
      "blur"
    ];

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Add scroll event separately for better handling
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    // Add visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup function
    return () => {
      // Clear timer
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }

      // Remove event listeners
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      
      // Cancel animation frame
      if (scrollAnimationFrameRef.current) {
        cancelAnimationFrame(scrollAnimationFrameRef.current);
      }
    };
  }, [location.pathname, isSubmitted]); // Re-run when path changes or submission status changes

  // Handle popup close
  const handleClose = () => {
    setShowPopup(false);
    popupOpenRef.current = false;

    // Mark popup as closed on current page
    markPopupClosedOnCurrentPage();

    // Reset activity time but don't start timer since popup is closed on this page
    lastActivityRef.current = Date.now();
    
    // Clear any existing timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission (keep your existing submit logic)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate CAPTCHA (case insensitive)
    if (formData.captcha.toUpperCase() !== captchaCode.toUpperCase()) {
      toast.error("Invalid CAPTCHA code. Please try again.");
      generateCaptcha();
      return;
    }

    // Validate form fields
    if (!formData.name.trim() || !formData.number.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate phone number
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.number.replace(/\D/g, ""))) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setIsLoading(true);

    try {
      const company_id = "2";

      const submitData = new FormData();
      submitData.append("company_id", company_id);
      submitData.append("inquiry_name", formData.name);
      submitData.append("inquiry_mobile", formData.number);
      submitData.append("inquiry_message", formData.message || "");

      const res = await axios.post(`${server_url}add_inquiry.php`, submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status === "1") {
        toast.success("Inquiry submitted successfully!");

        // Mark as submitted for this session only
        setIsSubmitted(true);
        localStorage.setItem("inquiry_submitted", "true");

        // Close popup
        setShowPopup(false);
        popupOpenRef.current = false;

        // Also mark as closed on current page
        markPopupClosedOnCurrentPage();

        // Reset form
        setFormData({
          name: "",
          number: "",
          message: "",
          captcha: "",
        });

        // Clear timer to prevent showing again in this session
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current);
          idleTimerRef.current = null;
        }
      } else {
        toast.error(res.data.error || "Failed to submit inquiry");
        generateCaptcha();
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Network error. Please try again.");
      generateCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if excluded page, already submitted, or popup not shown
  if (isExcludedPage() || !showPopup || isSubmitted) return null;
  
  return (
    // ... (Your existing JSX remains exactly the same)
    <>
      {/* Overlay */}
      <div
        className="idle-popup-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
          backdropFilter: "blur(5px)",
        }}
      >
        {/* Popup Container */}
        <div
          className="idle-popup-container"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "20px",
            maxWidth: "500px",
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e0e0e0",
            animation: "slideUp 0.3s ease-out",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: "15px 15px 12px",
              borderBottom: "1px solid #f0f0f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#ffffff",
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  color: "#333",
                  fontSize: "24px",
                  fontWeight: "700",
                }}
              >
                Get In Touch
              </h2>
              <p
                style={{
                  margin: "5px 0 0",
                  color: "#666",
                  fontSize: "14px",
                  fontWeight: "400",
                }}
              >
                We noticed you've been browsing for a while
              </p>
            </div>
            <button
              onClick={handleClose}
              style={{
                background: "rgba(0, 0, 0, 0.05)",
                border: "none",
                fontSize: "24px",
                color: "#666",
                cursor: "pointer",
                padding: "5px",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "rgba(255, 72, 58, 0.1)";
                e.target.style.color = "#ff483a";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "rgba(0, 0, 0, 0.05)";
                e.target.style.color = "#666";
              }}
            >
              ×
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ padding: "20px" }}>
            {/* Name Field */}
            <div style={{ marginBottom: "15px" }}>
              <label
                htmlFor="name"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#333",
                  fontWeight: "500",
                  fontSize: "15px",
                }}
              >
                Name <span style={{ color: "#ff483a" }}>*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "12px 15px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  backgroundColor: "#fff",
                  color: "#333",
                  transition: "all 0.3s",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#ff483a";
                  e.target.style.boxShadow = "0 0 0 2px rgba(255, 72, 58, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#ddd";
                  e.target.style.boxShadow = "none";
                }}
                placeholder="Enter your name"
              />
            </div>

            {/* Phone Number Field */}
            <div style={{ marginBottom: "15px" }}>
              <label
                htmlFor="number"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#333",
                  fontWeight: "500",
                  fontSize: "15px",
                }}
              >
                Phone Number <span style={{ color: "#ff483a" }}>*</span>
              </label>
              <input
                type="tel"
                id="number"
                name="number"
                value={formData.number}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "12px 15px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  backgroundColor: "#fff",
                  color: "#333",
                  transition: "all 0.3s",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#ff483a";
                  e.target.style.boxShadow = "0 0 0 2px rgba(255, 72, 58, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#ddd";
                  e.target.style.boxShadow = "none";
                }}
                placeholder="Enter your 10-digit phone number"
              />
            </div>

            {/* Message Field */}
            <div style={{ marginBottom: "15px" }}>
              <label
                htmlFor="message"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#333",
                  fontWeight: "500",
                  fontSize: "15px",
                }}
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="3"
                style={{
                  width: "100%",
                  padding: "12px 15px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  backgroundColor: "#fff",
                  color: "#333",
                  transition: "all 0.3s",
                  resize: "vertical",
                  minHeight: "80px",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#ff483a";
                  e.target.style.boxShadow = "0 0 0 2px rgba(255, 72, 58, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#ddd";
                  e.target.style.boxShadow = "none";
                }}
                placeholder="Enter your message (optional)"
              />
            </div>

            {/* CAPTCHA Section */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center", 
                marginBottom: "10px" 
              }}>
                <label
                  style={{
                    color: "#333",
                    fontWeight: "500",
                    fontSize: "15px",
                  }}
                >
                  Enter CAPTCHA Code <span style={{ color: "#ff483a" }}>*</span>
                </label>
                <button
                  type="button"
                  onClick={generateCaptcha}
                  style={{
                    background: "rgba(255, 72, 58, 0.1)",
                    color: "#ff483a",
                    border: "1px solid #ff483a",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.3s",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(255, 72, 58, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(255, 72, 58, 0.1)";
                  }}
                  title="Refresh CAPTCHA"
                >
                  <i className="fa fa-refresh" style={{ fontSize: "14px" }} />
                  Refresh
                </button>
              </div>

              {/* CAPTCHA Display & Input */}
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                {/* CAPTCHA Display */}
                <div
                  style={{
                    flex: 1,
                    background: "linear-gradient(135deg, #f5f5f5 0%, #f9f9f9 100%)",
                    border: "2px solid #ddd",
                    borderRadius: "8px",
                    padding: "12px",
                    textAlign: "center",
                    fontSize: "24px",
                    fontWeight: "bold",
                    letterSpacing: "3px",
                    color: "#333",
                    userSelect: "none",
                    fontFamily: "'Courier New', monospace, sans-serif",
                    minHeight: "50px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {captchaCode}
                </div>

                {/* CAPTCHA Input */}
                <input
                  type="text"
                  name="captcha"
                  value={formData.captcha}
                  onChange={handleChange}
                  required
                  maxLength="6"
                  placeholder="Enter code"
                  style={{
                    flex: 1,
                    padding: "12px 15px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "18px",
                    letterSpacing: "2px",
                    textAlign: "center",
                    backgroundColor: "#fff",
                    color: "#333",
                    boxSizing: "border-box",
                    transition: "all 0.3s",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#ff483a";
                    e.target.style.boxShadow = "0 0 0 2px rgba(255, 72, 58, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#ddd";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "15px",
                background: isLoading
                  ? "linear-gradient(135deg, #ccc 0%, #ddd 100%)"
                  : "linear-gradient(135deg, #ff483a 0%, #ff6b4a 100%)",
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: isLoading ? "not-allowed" : "pointer",
                transition: "all 0.3s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                boxShadow: "0 4px 15px rgba(255, 72, 58, 0.3)",
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 6px 20px rgba(255, 72, 58, 0.4)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 4px 15px rgba(255, 72, 58, 0.3)";
                }
              }}
            >
              {isLoading ? (
                <>
                  <i className="fa fa-spinner fa-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Inquiry"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Add CSS for animations and scrollbar */}
      <style jsx="true">{`
        input {
          background-color: #f4f1f1 !important;
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .idle-popup-container input:focus,
        .idle-popup-container textarea:focus {
          outline: none;
        }

        .idle-popup-container button:focus {
          outline: none;
        }

        /* Custom scrollbar for popup */
        .idle-popup-container::-webkit-scrollbar {
          width: 8px;
        }

        .idle-popup-container::-webkit-scrollbar-track {
          background: #f5f5f5;
          border-radius: 0 20px 20px 0;
        }

        .idle-popup-container::-webkit-scrollbar-thumb {
          background: #ddd;
          border-radius: 4px;
        }

        .idle-popup-container::-webkit-scrollbar-thumb:hover {
          background: #ccc;
        }

        /* Placeholder styling */
        .idle-popup-container input::placeholder,
        .idle-popup-container textarea::placeholder {
          color: #999;
          opacity: 0.7;
        }
      `}</style>
    </>
  );
};

export default IdlePopup;